const API_BASE = import.meta.env.PROD
  ? 'https://feedback-api.fujistyleapp.com/api'
  : '/api';

export interface CreateFeedbackData {
  type: 'bug' | 'suggestion';
  locale: string;
  title: string;
  description: string;
  email?: string;
  consent: boolean;
  platform?: string;
  app_version?: string;
  device_model?: string;
  os_version?: string;
  frequency?: string;
  severity?: string;
  steps?: string;
  expected?: string;
  actual?: string;
  category?: string;
  priority?: string;
  scenario?: string;
}

export interface FeedbackResponse {
  ok: boolean;
  data?: {
    id: string;
    ref_code: string;
    uploadErrors?: string[];
  };
  error?: string;
}

export interface FeedbackListItem {
  id: string;
  ref_code: string;
  type: 'bug' | 'suggestion';
  status: 'new' | 'in_progress' | 'resolved' | 'ignored';
  locale: string;
  title: string;
  description: string;
  email?: string;
  platform?: string;
  attachments_count: number;
  created_at: number;
  updated_at: number;
}

export interface FeedbackDetail extends FeedbackListItem {
  app_version?: string;
  device_model?: string;
  os_version?: string;
  frequency?: string;
  severity?: string;
  steps?: string;
  expected?: string;
  actual?: string;
  category?: string;
  priority?: string;
  scenario?: string;
  admin_note?: string;
  attachments: Array<{
    id: string;
    object_key: string;
    original_name?: string;
    content_type: string;
    size: number;
    preview_url: string;
  }>;
}

export interface FeedbackListResponse {
  ok: boolean;
  data?: {
    items: FeedbackListItem[];
    total: number;
    page: number;
    pageSize: number;
  };
  error?: string;
}

export interface FeedbackDetailResponse {
  ok: boolean;
  data?: FeedbackDetail;
  error?: string;
}

// Submit feedback
export async function submitFeedback(
  data: CreateFeedbackData,
  files: File[]
): Promise<FeedbackResponse> {
  const formData = new FormData();

  // Add text fields
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      formData.append(key, String(value));
    }
  });

  // Add files
  files.forEach(file => {
    formData.append('attachments', file);
  });

  const response = await fetch(`${API_BASE}/feedback`, {
    method: 'POST',
    body: formData
  });

  return response.json();
}

// Admin: List feedback
export async function listFeedback(
  params: {
    page?: number;
    pageSize?: number;
    type?: string;
    status?: string;
    q?: string;
    hasAttachments?: boolean;
  },
  credentials: { username: string; password: string }
): Promise<FeedbackListResponse> {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const response = await fetch(`${API_BASE}/admin/feedback?${searchParams}`, {
    headers: {
      'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
    }
  });

  if (response.status === 401) {
    throw new Error('Unauthorized');
  }

  return response.json();
}

// Admin: Get feedback detail
export async function getFeedback(
  id: string,
  credentials: { username: string; password: string }
): Promise<FeedbackDetailResponse> {
  const response = await fetch(`${API_BASE}/admin/feedback/${id}`, {
    headers: {
      'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
    }
  });

  if (response.status === 401) {
    throw new Error('Unauthorized');
  }

  return response.json();
}

// Admin: Update feedback status
export async function updateFeedback(
  id: string,
  data: { status?: string; admin_note?: string },
  credentials: { username: string; password: string }
): Promise<{ ok: boolean; error?: string }> {
  const response = await fetch(`${API_BASE}/admin/feedback/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
    },
    body: JSON.stringify(data)
  });

  if (response.status === 401) {
    throw new Error('Unauthorized');
  }

  return response.json();
}
